import { NodeIndexOutlined, UserOutlined } from "@ant-design/icons"
import { Avatar, Button, Card, Flex, Layout, Spin, Typography } from "antd"
import React, { useEffect } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useSearchParams } from "react-router-dom"

import Images from "../../../Images"
import LoginForm from "../../../components/Forms/LoginForm"
import PrivacyPolicy from "../../../components/PrivacyPolicy"
import { generateDocumentTitle } from "../../../constants/document-title"
import { useNotifications } from "../../../hooks/use-notifications"
import { type RootState } from "../../../store.ts"
import { getEntityName } from "../../../utils/user/get-entity-name"
import { authErrorReset, authSetProp } from "../../LoginPage/redux/auth.slice"
import {
  whatsappGoogleLogin,
  whatsappLogin
} from "../../LoginPage/redux/auth.thunks"
import { SuccessConnectionPopup } from "./SuccessConnectionPopup.tsx"

const { Title, Text, Paragraph } = Typography

function WhatsAppLoginPage({
  error,
  userInfo,
  fetching,
  token,
  isWhatsAppConnected,
  whatsAppLogin,
  googleLogin,
  setProp
}: ConnectedProps<typeof connector>) {
  const notification = useNotifications()
  const [searchParams] = useSearchParams()

  const clearConnectedState = () =>
    setProp({ key: "isWhatsAppConnected", value: false })

  const whatsapp_id = searchParams.get("whatsapp_id")
  const userName = userInfo && getEntityName(userInfo)

  function handleSubmit({
    email,
    password
  }: {
    email: string
    password: string
  }) {
    if (!whatsapp_id) {
      notification.error({ message: "Whatsapp ID not found" })
      return
    }

    whatsAppLogin({ email, password, whatsapp_id })
  }

  const handleConnectToAccount = () => {
    if (!whatsapp_id) {
      notification.error({ message: "Whatsapp ID not found" })
      return
    }

    whatsAppLogin({ token, whatsapp_id })
  }

  const handleSocialAuth = (data = {}) => {
    if (!whatsapp_id) {
      notification.error({ message: "Whatsapp ID not found" })
      return
    }

    googleLogin({ ...data, whatsapp_id })
  }

  useEffect(() => {
    generateDocumentTitle("Login")
    clearConnectedState()
  }, [])

  return (
    <Layout className={"min-h-svh auth-bg"}>
      {isWhatsAppConnected && (
        <SuccessConnectionPopup onClose={clearConnectedState} />
      )}
      <Spin size="large" spinning={fetching}>
        <Flex vertical align="center" justify="center" className={"min-h-svh"}>
          <Card
            className="login-card shadow-sm border-gray-200/70"
            style={{ margin: "auto", width: "100%", maxWidth: "520px" }}
          >
            <Flex vertical align="center" style={{ marginBottom: 16 }}>
              <div className={"flex items-center gap-4 pb-2"}>
                <img src={Images.logo} alt="Logo" className="h-16 md:h-20" />
                {/*<Flex vertical>*/}
                {/*  <Text className="font-semibold text-2xl md:text-4xl !leading-relaxed">*/}
                {/*    EZBuild*/}
                {/*  </Text>*/}
                {/*  <Text className={"text-sm md:text-lg font-semibold"}>*/}
                {/*    Simple. Easy. Smart.*/}
                {/*  </Text>*/}
                {/*</Flex>*/}
              </div>

              {!userInfo && (
                <>
                  <Title
                    level={1}
                    style={{ marginBottom: 6, fontSize: "28px" }}
                  >
                    Connect Your Account
                  </Title>
                  <Paragraph
                    type="secondary"
                    className={"text-center"}
                    style={{ fontSize: 16, margin: 0 }}
                  >
                    Link your account to start building with EZBuild platform.
                  </Paragraph>
                </>
              )}
            </Flex>

            {userInfo && (
              <Flex vertical gap={"middle"}>
                {/* User Profile Section */}
                <Flex vertical align={"center"} gap={"small"}>
                  <div className="relative">
                    <Avatar
                      size={70}
                      src={userInfo?.avatar}
                      icon={<UserOutlined />}
                      className="border-2 border-white shadow-lg"
                    />
                    <div className="absolute bottom-0 right-0 bg-green-500 size-4 rounded-full border-2 border-white"></div>
                  </div>
                  <Title level={4} className={"!mb-2"}>
                    {userName || userInfo?.email}
                  </Title>

                  <Button
                    disabled={fetching}
                    type="primary"
                    color="cyan"
                    icon={<NodeIndexOutlined />}
                    size="large"
                    onClick={handleConnectToAccount}
                  >
                    Connect To WhatsApp
                  </Button>
                </Flex>

                {/* Divider */}
                <Flex align="center" gap="middle">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <Text type="secondary" className="px-4 text-sm font-medium">
                    Or
                  </Text>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </Flex>

                {/* Alternative Login Text */}
                <div className="text-center">
                  <Text type="secondary">
                    Want to use a different account? Enter your credentials
                    below to sign in with email and password.
                  </Text>
                </div>
              </Flex>
            )}

            <LoginForm
              loading={fetching}
              // providers={[]}
              showForgotPassword={false}
              onSubmit={handleSubmit}
              error={error?.message}
              otpRequired={false}
              resetError={() => console.log("resetError")}
              socialAuth={handleSocialAuth}
            />
          </Card>
          <PrivacyPolicy />
        </Flex>
      </Spin>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => ({
  token: state.auth.accessToken,
  userInfo: state.auth.userInfo,
  error: state.auth.error,
  isWhatsAppConnected: state.auth.isWhatsAppConnected,
  fetching: state.auth.fetching
})

const mapDispatchToProps = {
  setProp: authSetProp,
  resetError: authErrorReset,
  googleLogin: whatsappGoogleLogin,
  whatsAppLogin: whatsappLogin
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(WhatsAppLoginPage)
